import { ApiClientError } from '@/lib/api';

/**
 * Map a Worker AI error code to an operator-friendly message. Specific codes
 * (auth, rate limit, schema, timeout) come from the Worker's classifyOpenAiError;
 * we keep the upstream message visible because hiding it behind "AI is
 * temporarily unavailable" is what masked the Quick Capture strict-mode
 * regression for weeks.
 */
export function formatAiError(err: unknown, fallback = 'AI request failed'): string {
  if (err instanceof ApiClientError) {
    switch (err.code) {
      case 'openai_not_configured':
        return 'OpenAI is not configured on the Worker. Use "Append as plain note" instead.';
      case 'ai_auth_error':
        return err.message || 'OpenAI rejected the API key. Check the Worker secret.';
      case 'ai_rate_limited':
        return 'OpenAI is rate-limiting requests. Try again in a minute.';
      case 'ai_timeout':
        return 'AI request timed out. Try again, or use the non-AI fallback.';
      case 'ai_schema_error':
        // Surface the upstream message — this is exactly the class of failure
        // that needs to be debuggable rather than hidden behind a generic message.
        return err.message || 'AI rejected the request schema. Check the Worker logs.';
      case 'ai_unavailable':
        return err.message
          ? `AI is temporarily unavailable. ${err.message}`
          : 'AI is temporarily unavailable. Try again shortly, or use "Append as plain note" instead.';
      case 'budget_exceeded':
        return err.message || 'Daily Copilot budget reached. Resets at UTC midnight.';
      case 'validation_error':
        return `Validation failed: ${err.message}`;
      default:
        return err.message || fallback;
    }
  }
  return err instanceof Error ? err.message : fallback;
}

export function formatAiWarnings(warnings: string[] | undefined, logId?: string | null): string[] {
  const next = [...(warnings ?? [])];
  if (logId) next.push(`Reference log: ${logId}`);
  return next;
}
